/**
 * Phase 308 — Geofence Location Card
 * ===================================
 *
 * Drop-in card for the Settings "Location & Geofence" tab. Renders an
 * interactive Leaflet map with a draggable marker + translucent radius
 * circle, and persists to PUT /api/academy/geofence-location.
 *
 * Open-source dependencies only: `react-leaflet` v4 + `leaflet`.
 * Tile layer: OpenStreetMap public tiles (no API key required).
 *
 * Why Leaflet (not Mapbox/Google):
 *   • Zero API-key blockers — academy owners can use the feature
 *     immediately without procurement / billing setup.
 *   • Open-source, MIT licensed, mature React bindings.
 *   • Lazy-loaded so the map JS only ships when the tab is opened.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { MapPin, Save, Crosshair, Info, CheckCircle2 } from 'lucide-react';

// ── Default-marker icon fix (webpack strips Leaflet's bundled images) ─
// Self-hosted under /public/leaflet/ to remove the unpkg.com CDN
// dependency — academies on locked-down intranets still see the marker.
const DEFAULT_ICON = L.icon({
  iconUrl: '/leaflet/marker-icon.png',
  iconRetinaUrl: '/leaflet/marker-icon-2x.png',
  shadowUrl: '/leaflet/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DEFAULT_ICON;

const DEFAULT_LAT = 12.9716;   // Bengaluru — sensible IN default
const DEFAULT_LNG = 77.5946;
const DEFAULT_RADIUS = 100;
const MIN_RADIUS = 25;
const MAX_RADIUS = 2000;

function _isValidCoord(lat, lng) {
  return (
    typeof lat === 'number' && typeof lng === 'number' &&
    !Number.isNaN(lat) && !Number.isNaN(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
  );
}

// Helper component — re-centers the map when the parent updates coords
// (e.g., after "Use my location" button or initial load).
function FlyToOnChange({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (_isValidCoord(lat, lng)) {
      map.flyTo([lat, lng], map.getZoom() < 17 ? 17 : map.getZoom(), { duration: 0.4 });
    }
  }, [lat, lng, map]);
  return null;
}

export default function GeofenceLocationCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lat, setLat] = useState(DEFAULT_LAT);
  const [lng, setLng] = useState(DEFAULT_LNG);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [hasSaved, setHasSaved] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const markerRef = useRef(null);

  // Hydrate from server on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/academy/geofence-location');
        if (cancelled) return;
        if (data?.configured) {
          if (_isValidCoord(data.lat, data.lng)) {
            setLat(data.lat);
            setLng(data.lng);
            setHasSaved(true);
          }
          if (data.radius_meters) setRadius(data.radius_meters);
          if (data.updated_at) setLastUpdatedAt(data.updated_at);
        }
      } catch (_) {
        // 403 / network → silently keep the defaults; the save button
        // will surface a friendlier error if the owner tries to persist.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const markerEventHandlers = useMemo(() => ({
    dragend(e) {
      const m = e?.target;
      if (!m) return;
      const { lat: newLat, lng: newLng } = m.getLatLng();
      setLat(newLat);
      setLng(newLng);
    },
  }), []);

  const useMyLocation = () => {
    if (!navigator?.geolocation?.getCurrentPosition) {
      toast.error('Geolocation not supported by this browser');
      return;
    }
    toast.message('Getting your current location…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords || {};
        if (_isValidCoord(latitude, longitude)) {
          setLat(latitude);
          setLng(longitude);
          toast.success('Marker centred on your current location');
        }
      },
      (err) => {
        if (err?.code === 1) toast.error('Geolocation permission denied');
        else toast.error("Couldn't read your location");
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const handleSave = async () => {
    if (!_isValidCoord(lat, lng)) {
      toast.error('Please drop the marker on a valid location');
      return;
    }
    if (radius < MIN_RADIUS || radius > MAX_RADIUS) {
      toast.error(`Radius must be between ${MIN_RADIUS}m and ${MAX_RADIUS}m`);
      return;
    }
    setSaving(true);
    try {
      await api.put('/academy/geofence-location', {
        lat: Number(lat.toFixed(6)),
        lng: Number(lng.toFixed(6)),
        radius_meters: Math.round(radius),
      });
      setHasSaved(true);
      setLastUpdatedAt(new Date().toISOString());
      toast.success(`Geofence saved — coaches inside ${Math.round(radius)}m will auto clock in.`);
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 400) toast.error(detail || 'Invalid coordinates');
      else if (code === 403) toast.error('Only academy owners can configure the geofence.');
      else toast.error('Could not save geofence — please retry.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="geofence-card-loading">
        <CardContent className="py-8 space-y-3">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-emerald-200/60" data-testid="geofence-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-emerald-600" />
          Geofence Location
          {hasSaved ? (
            <Badge variant="outline" className="ml-2 border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]" data-testid="geofence-saved-badge">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Configured
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-2 border-amber-200 bg-amber-50 text-amber-700 text-[10px]" data-testid="geofence-unsaved-badge">
              Not set
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          Drag the pin to your turf. Coaches inside this circle on their phone PWA will be auto-clocked in;
          stepping outside for 2 consecutive pings auto-clocks them out.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Map */}
        <div className="rounded-lg overflow-hidden border border-slate-200 shadow-sm" style={{ height: 360 }} data-testid="geofence-map-container">
          <MapContainer
            center={[lat, lng]}
            zoom={17}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker
              position={[lat, lng]}
              draggable
              ref={markerRef}
              eventHandlers={markerEventHandlers}
            />
            <Circle
              center={[lat, lng]}
              radius={radius}
              pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 0.15, weight: 2 }}
            />
            <FlyToOnChange lat={lat} lng={lng} />
          </MapContainer>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="geofence-lat" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Latitude</Label>
            <Input
              id="geofence-lat"
              type="number"
              step="0.000001"
              value={lat.toFixed(6)}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) setLat(v);
              }}
              className="mt-1 text-sm tabular-nums"
              data-testid="geofence-lat-input"
            />
          </div>
          <div>
            <Label htmlFor="geofence-lng" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Longitude</Label>
            <Input
              id="geofence-lng"
              type="number"
              step="0.000001"
              value={lng.toFixed(6)}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) setLng(v);
              }}
              className="mt-1 text-sm tabular-nums"
              data-testid="geofence-lng-input"
            />
          </div>
          <div>
            <Label htmlFor="geofence-radius" className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
              <span>Radius</span>
              <span className="tabular-nums text-emerald-700 font-bold" data-testid="geofence-radius-value">{Math.round(radius)} m</span>
            </Label>
            <Slider
              id="geofence-radius"
              value={[radius]}
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              step={5}
              onValueChange={(v) => setRadius(v[0])}
              className="mt-3"
              data-testid="geofence-radius-slider"
            />
            <p className="text-[10px] text-slate-400 mt-1">25 m (small studio) → 2000 m (multi-pitch complex)</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 items-center justify-between pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={useMyLocation}
            data-testid="geofence-use-my-location"
          >
            <Crosshair className="h-4 w-4 mr-1.5" /> Use my current location
          </Button>
          <div className="flex items-center gap-3">
            {lastUpdatedAt ? (
              <span className="text-[11px] text-slate-400 hidden sm:inline">
                Last updated {new Date(lastUpdatedAt).toLocaleString()}
              </span>
            ) : null}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="geofence-save-btn"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {saving ? 'Saving…' : 'Save Location'}
            </Button>
          </div>
        </div>

        {/* Info banner */}
        <div className="flex gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
          <Info className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <div>
            <strong className="text-slate-700">Tip:</strong> If your academy has multiple pitches, place the pin at the centre and increase the radius to cover all of them. The geofence respects WGS84 coordinates with sub-metre precision.
            Enable <strong>Geofence Staff Tracking</strong> in Automation Flags for this to take effect.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
